import React, { useCallback, useEffect, useState } from "react";
import {
  Zap, Loader2, Plus, Trash2, Copy, Code, MessageCircle, FileText,
  KeyRound, Eye, EyeOff, X, Save, RefreshCw, Check, ExternalLink,
  Phone,
} from "lucide-react";
import { api } from "../../api";

/**
 * IntegrationsModule — admin-only tab inside HEXA Admin AI.
 * Two sections:
 *   1. Widgets  → embeddable HEXA Form + HEXA AI Assistant that feed CRM
 *   2. Secrets  → encrypted vault for provider keys (Zeptomail / DialNexa /
 *                 Composio WhatsApp / custom)
 */
const WIDGET_META = {
  form:      { Icon: FileText,      bg: "linear-gradient(135deg,#0ea5e9,#38bdf8)", label: "HEXA Form" },
  assistant: { Icon: MessageCircle, bg: "linear-gradient(135deg,#8b5cf6,#a855f7)", label: "HEXA AI Assistant" },
  "ai-phone": { Icon: Phone,        bg: "linear-gradient(135deg,#22c55e,#16a34a)", label: "AI Phone Icon" },
};
const widgetMeta = (kind) => WIDGET_META[kind] || WIDGET_META.form;

export default function IntegrationsModule() {
  const [tab, setTab] = useState("widgets");
  return (
    <div className="space-y-4" data-testid="admin-integrations">
      <div className="flex flex-wrap gap-2">
        {[
          { k: "widgets", label: "Website widgets", Icon: Code,     tint: "#38bdf8" },
          { k: "ivr",     label: "IVR ring-groups", Icon: Phone,    tint: "#f59e0b" },
          { k: "secrets", label: "Secrets vault",   Icon: KeyRound, tint: "#a855f7" },
        ].map(({ k, label, Icon, tint }) => {
          const active = tab === k;
          return (
            <button
              key={k}
              data-testid={`admin-integrations-tab-${k}`}
              onClick={() => setTab(k)}
              className="px-3 py-1.5 rounded-lg mono text-[10px] uppercase tracking-[0.22em] flex items-center gap-1.5 transition font-medium"
              style={{
                background: active ? `linear-gradient(120deg, ${tint}, ${tint}cc)` : "#ffffff",
                color: active ? "#ffffff" : "#334155",
                border: `1px solid ${active ? tint : "#e2e8f0"}`,
              }}
            >
              <Icon size={11} color={active ? "#ffffff" : tint} /> {label}
            </button>
          );
        })}
      </div>

      {tab === "widgets" && <WidgetsPanel />}
      {tab === "ivr" && <IvrPanel />}
      {tab === "secrets" && <SecretsPanel />}
    </div>
  );
}

/* ─────────────────────────────  WIDGETS  ───────────────────────────── */

function WidgetsPanel() {
  const [widgets, setWidgets] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [creating, setCreating] = useState(null); // "form" | "assistant" | null
  const [editing, setEditing] = useState(null);
  const [embedFor, setEmbedFor] = useState(null); // widget object
  const [analyticsFor, setAnalyticsFor] = useState(null);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const r = await api.get("/admin/widgets");
      setWidgets(r.data || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async (kind) => {
    const prompts = { form: "Name your form (e.g. 'Website enquiry')", assistant: "Name your AI assistant (e.g. 'Sales copilot')", "ai-phone": "Name your AI Phone Icon (e.g. 'Website voice agent')" };
    const name = prompt(prompts[kind] || "Name this widget");
    if (!name) return;
    setBusy(true); setErr("");
    try {
      await api.post("/admin/widgets", { kind, name, active: true });
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); setCreating(null); }
  };

  const remove = async (w) => {
    if (!window.confirm(`Delete "${w.name}"? Any embedded copies will stop working.`)) return;
    try { await api.delete(`/admin/widgets/${w.id}`); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  const toggleActive = async (w) => {
    try {
      await api.put(`/admin/widgets/${w.id}`, { kind: w.kind, name: w.name, fields: w.fields, theme: w.theme, thank_you: w.thank_you, active: !w.active });
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-slate-800">
          <div className="text-base font-semibold">Website widgets</div>
          <div className="text-xs text-slate-500 mt-0.5">Drop-in forms and AI assistants that stream leads into CRM.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
          <button data-testid="admin-widget-new-form" onClick={() => create("form")}
            className="text-[11px] px-3 py-1.5 rounded text-white font-medium flex items-center gap-1"
            style={{ background: "linear-gradient(120deg,#0ea5e9,#38bdf8)" }}>
            <Plus size={12} /> New form
          </button>
          <button data-testid="admin-widget-new-assistant" onClick={() => create("assistant")}
            className="text-[11px] px-3 py-1.5 rounded text-white font-medium flex items-center gap-1"
            style={{ background: "linear-gradient(120deg,#8b5cf6,#a855f7)" }}>
            <Plus size={12} /> New AI assistant
          </button>
          <button data-testid="admin-widget-new-ai-phone" onClick={() => create("ai-phone")}
            className="text-[11px] px-3 py-1.5 rounded text-white font-medium flex items-center gap-1"
            style={{ background: "linear-gradient(120deg,#22c55e,#16a34a)" }}>
            <Plus size={12} /> New AI Phone Icon
          </button>
        </div>
      </div>

      {err && <div className="text-red-600 text-xs mono">{err}</div>}

      {busy && !widgets.length && (
        <div className="text-slate-400 text-xs py-6 text-center flex items-center justify-center gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading widgets…
        </div>
      )}

      {!busy && !widgets.length && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-8 px-6 text-center">
          <Zap size={22} className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700">No widgets yet</div>
          <div className="text-[11.5px] text-slate-500 mt-1">Create your first HEXA form or AI assistant to start collecting leads from any website.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {widgets.map((w) => (
          <div key={w.id} data-testid={`admin-widget-${w.id}`}
            className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <div className="grid place-items-center rounded-md" style={{
                width: 36, height: 36,
                background: widgetMeta(w.kind).bg,
                color: "#fff",
              }}>
                {React.createElement(widgetMeta(w.kind).Icon, { size: 16 })}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 font-semibold text-[13px] truncate">{w.name}</div>
                <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500 mt-0.5">
                  {widgetMeta(w.kind).label} · {w.submissions || 0} leads
                </div>
              </div>
              <span className="mono text-[9px] px-2 py-0.5 rounded-full uppercase tracking-widest"
                style={{
                  color: w.active ? "#059669" : "#94a3b8",
                  background: w.active ? "rgba(5,150,105,0.10)" : "#f1f5f9",
                  border: `1px solid ${w.active ? "rgba(5,150,105,0.35)" : "#e2e8f0"}`,
                }}>
                {w.active ? "Live" : "Paused"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <button onClick={() => setEmbedFor(w)} data-testid={`admin-widget-embed-${w.id}`}
                className="text-[11px] px-3 py-1.5 rounded bg-slate-900 text-white flex items-center gap-1 hover:bg-slate-800">
                <Code size={11} /> Get code
              </button>
              <button onClick={() => setEditing(w)} data-testid={`admin-widget-edit-${w.id}`}
                className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                Edit
              </button>
              <button onClick={() => setAnalyticsFor(w)} data-testid={`admin-widget-analytics-${w.id}`}
                className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                Analytics
              </button>
              <button onClick={() => toggleActive(w)}
                className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">
                {w.active ? "Pause" : "Resume"}
              </button>
              <button onClick={() => remove(w)} data-testid={`admin-widget-delete-${w.id}`}
                className="text-[11px] px-3 py-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50 flex items-center gap-1 ml-auto">
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <WidgetEditor
          widget={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}

      {embedFor && (
        <EmbedCodeModal
          widget={embedFor}
          onClose={() => setEmbedFor(null)}
        />
      )}

      {analyticsFor && (
        <WidgetAnalyticsModal
          widget={analyticsFor}
          onClose={() => setAnalyticsFor(null)}
        />
      )}
    </div>
  );
}

function WidgetAnalyticsModal({ widget, onClose }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      setBusy(true); setErr("");
      try { const r = await api.get(`/admin/widgets/${widget.id}/analytics`); setData(r.data); }
      catch (e) { setErr(e?.response?.data?.detail || e.message); }
      finally { setBusy(false); }
    })();
  }, [widget.id]);

  const stages = data ? [
    { key: "impressions",  label: "Impressions",  value: data.impressions,  tint: "#38bdf8" },
    { key: "submissions",  label: "Submissions",  value: data.submissions,  tint: "#a855f7" },
    { key: "leads",        label: "CRM leads",    value: data.leads,        tint: "#8b5cf6" },
    { key: "won",          label: "Deals won",    value: data.won,          tint: "#22c55e" },
  ] : [];
  const max = stages.length ? Math.max(1, ...stages.map(s => s.value || 0)) : 1;

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)" }}
        data-testid="widget-analytics-modal">
        <div className="flex items-center gap-3 mb-4">
          <div className="grid place-items-center rounded-md" style={{
            width: 36, height: 36,
            background: widgetMeta(widget.kind).bg,
            color: "#fff",
          }}>
            {React.createElement(widgetMeta(widget.kind).Icon, { size: 16 })}
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-800">{widget.name}</div>
            <div className="text-[11.5px] text-slate-500 mono">{widgetMeta(widget.kind).label} funnel</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={16} /></button>
        </div>

        {busy && <div className="text-slate-500 text-sm flex items-center gap-2 py-6 justify-center"><Loader2 size={14} className="animate-spin" /> Loading analytics…</div>}
        {err && <div className="text-red-600 text-xs mono">{err}</div>}

        {data && (
          <>
            <div className="space-y-2 mb-4">
              {stages.map((s) => {
                const pct = Math.max(6, Math.round(((s.value || 0) / max) * 100));
                return (
                  <div key={s.key} className="flex items-center gap-3">
                    <div className="w-24 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">{s.label}</div>
                    <div className="flex-1 h-8 rounded-md relative overflow-hidden bg-slate-100">
                      <div className="h-full" style={{
                        width: `${pct}%`,
                        background: `linear-gradient(120deg, ${s.tint}, ${s.tint}bb)`,
                      }} />
                      <div className="absolute inset-0 grid place-items-center text-[12px] font-semibold text-slate-800">
                        {s.value}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="grid grid-cols-2 gap-2 mb-4">
              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Conversion</div>
                <div className="text-slate-800 font-semibold text-lg mt-1">{data.conversion_rate_pct}%</div>
                <div className="text-[10.5px] text-slate-500">impressions → submissions</div>
              </div>
              <div className="rounded-md border border-slate-200 p-3">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Win rate</div>
                <div className="text-slate-800 font-semibold text-lg mt-1">{data.win_rate_pct}%</div>
                <div className="text-[10.5px] text-slate-500">leads → deals won</div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Last 7 days</div>
              {data.trend_last_7d && data.trend_last_7d.length > 0 ? (
                <div className="flex items-end gap-1 h-16">
                  {data.trend_last_7d.map((d) => {
                    const dmax = Math.max(1, ...data.trend_last_7d.map(x => x.count));
                    const h = Math.max(4, Math.round((d.count / dmax) * 60));
                    return (
                      <div key={d.day} className="flex-1 flex flex-col items-center gap-1">
                        <div className="w-full rounded" style={{ height: h, background: "linear-gradient(180deg,#8b5cf6,#38bdf8)" }} title={`${d.day}: ${d.count}`} />
                        <div className="text-[9px] text-slate-400 mono">{d.day.slice(5)}</div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-slate-400 text-[11.5px]">No submissions in the last 7 days.</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const FIELD_TYPES = [
  { v: "text",     label: "Text" },
  { v: "email",    label: "Email" },
  { v: "phone",    label: "Phone" },
  { v: "number",   label: "Number" },
  { v: "textarea", label: "Long text" },
  { v: "select",   label: "Select" },
];

function WidgetEditor({ widget, onClose, onSaved }) {
  const [f, setF] = useState({
    name: widget.name,
    thank_you: widget.thank_you || "",
    active: widget.active,
    fields: [...(widget.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0)),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setField = (idx, patch) => {
    setF({ ...f, fields: f.fields.map((x, i) => i === idx ? { ...x, ...patch } : x) });
  };
  const move = (idx, dir) => {
    const next = [...f.fields];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((x, i) => { x.order = i + 1; });
    setF({ ...f, fields: next });
  };
  const addField = () => {
    setF({ ...f, fields: [...f.fields, { key: `field_${f.fields.length + 1}`, label: "New field", type: "text", required: false, order: f.fields.length + 1 }] });
  };
  const removeField = (idx) => {
    if (widget.kind === "assistant" && ["name", "email", "phone"].includes(f.fields[idx].key)) {
      alert("Assistant widgets require Name / Email / Phone gate fields.");
      return;
    }
    setF({ ...f, fields: f.fields.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      await api.put(`/admin/widgets/${widget.id}`, {
        kind: widget.kind,
        name: f.name,
        fields: f.fields,
        theme: widget.theme || {},
        thank_you: f.thank_you,
        active: f.active,
      });
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)" }}>
        <div className="flex items-center gap-3 px-5 py-4 border-b border-slate-200">
          <div className="grid place-items-center rounded-md" style={{
            width: 32, height: 32,
            background: widgetMeta(widget.kind).bg,
            color: "#fff",
          }}>
            {React.createElement(widgetMeta(widget.kind).Icon, { size: 14 })}
          </div>
          <div className="flex-1">
            <div className="text-sm font-semibold text-slate-800">Edit {widgetMeta(widget.kind).label}</div>
            <div className="text-[11px] text-slate-500 mono">{widget.id}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={16} /></button>
        </div>

        <div className="p-5 overflow-y-auto flex-1 space-y-4">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Widget name</div>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400"
              data-testid="widget-edit-name" />
          </label>

          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Thank-you message</div>
            <input value={f.thank_you} onChange={(e) => setF({ ...f, thank_you: e.target.value })}
              placeholder="Thanks! We'll be in touch shortly."
              className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400" />
          </label>

          {widget.kind === "ai-phone" ? (
            <div className="rounded-lg border border-dashed border-emerald-300 bg-emerald-50 p-4 text-[12.5px] text-emerald-800">
              AI Phone Icon widgets don&apos;t use static fields — the AI voice agent naturally asks for name, phone, email, and product interest during the call.
            </div>
          ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Fields ({f.fields.length})</div>
              <button onClick={addField} data-testid="widget-add-field"
                className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                <Plus size={11} /> Add field
              </button>
            </div>
            <div className="space-y-2">
              {f.fields.map((fld, idx) => (
                <div key={idx} className="border border-slate-200 rounded-md p-3 bg-slate-50 grid grid-cols-12 gap-2 items-start">
                  <input value={fld.label} onChange={(e) => setField(idx, { label: e.target.value })}
                    placeholder="Label" className="col-span-4 px-2 py-1.5 rounded border border-slate-200 bg-white text-sm outline-none" />
                  <input value={fld.key} onChange={(e) => setField(idx, { key: e.target.value.replace(/[^a-z0-9_]/gi, "_").toLowerCase() })}
                    placeholder="key" className="col-span-3 px-2 py-1.5 rounded border border-slate-200 bg-white text-xs mono outline-none" />
                  <select value={fld.type} onChange={(e) => setField(idx, { type: e.target.value })}
                    className="col-span-2 px-2 py-1.5 rounded border border-slate-200 bg-white text-xs outline-none">
                    {FIELD_TYPES.map(t => <option key={t.v} value={t.v}>{t.label}</option>)}
                  </select>
                  <label className="col-span-1 flex items-center gap-1 text-[10.5px] text-slate-600">
                    <input type="checkbox" checked={fld.required} onChange={(e) => setField(idx, { required: e.target.checked })} /> req
                  </label>
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <button onClick={() => move(idx, -1)} disabled={idx === 0}
                      className="text-slate-500 hover:text-slate-800 disabled:opacity-30 text-xs">↑</button>
                    <button onClick={() => move(idx, +1)} disabled={idx === f.fields.length - 1}
                      className="text-slate-500 hover:text-slate-800 disabled:opacity-30 text-xs">↓</button>
                    <button onClick={() => removeField(idx)}
                      className="text-rose-500 hover:text-rose-700"><Trash2 size={12} /></button>
                  </div>
                  {fld.type === "select" && (
                    <input value={(fld.options || []).join(", ")}
                      onChange={(e) => setField(idx, { options: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })}
                      placeholder="Option 1, Option 2, Option 3"
                      className="col-span-12 px-2 py-1.5 rounded border border-slate-200 bg-white text-xs outline-none" />
                  )}
                </div>
              ))}
            </div>
          </div>
          )}

          {err && <div className="text-red-600 text-xs mono">{err}</div>}
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex items-center justify-end gap-2 bg-slate-50">
          <button onClick={onClose} className="text-[12px] text-slate-500 px-3 py-2">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="widget-edit-save"
            className="text-[12px] px-4 py-2 rounded text-white font-medium flex items-center gap-1.5"
            style={{ background: "linear-gradient(120deg,#8b5cf6,#38bdf8)" }}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

function EmbedCodeModal({ widget, onClose }) {
  const [copied, setCopied] = useState(false);
  const base = process.env.REACT_APP_BACKEND_URL || "";
  const src = `${base}/api/public/widget/${widget.id}.js`;
  const snippet = widget.kind === "form"
    ? `<!-- HEXA Form: ${widget.name} -->\n<div id="hexa-widget-${widget.id}"></div>\n<script src="${src}" async></script>`
    : widget.kind === "ai-phone"
    ? `<!-- HEXA AI Phone Icon: ${widget.name} -->\n<script src="${src}" async></script>`
    : `<!-- HEXA AI Assistant: ${widget.name} -->\n<script src="${src}" async></script>`;

  const copy = async () => {
    try { await navigator.clipboard.writeText(snippet); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (_) { window.prompt("Copy this snippet:", snippet); }
  };

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()}
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)" }}>
        <div className="flex items-center gap-3 mb-3">
          <div className="grid place-items-center rounded-md" style={{
            width: 36, height: 36,
            background: widgetMeta(widget.kind).bg,
            color: "#fff",
          }}>
            <Code size={16} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-800">Embed on your website</div>
            <div className="text-[11.5px] text-slate-500">Copy this code and paste it anywhere in your site&apos;s HTML.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={16} /></button>
        </div>

        <div className="bg-slate-900 rounded-md p-4 relative">
          <button onClick={copy} data-testid="widget-embed-copy"
            className="absolute top-2 right-2 text-[11px] px-3 py-1.5 rounded bg-white text-slate-900 font-medium flex items-center gap-1 hover:bg-slate-100">
            {copied ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
          </button>
          <pre className="text-[12px] text-emerald-300 mono whitespace-pre-wrap break-all pr-16" style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
{snippet}
          </pre>
        </div>

        <div className="mt-4 space-y-2 text-[12.5px] text-slate-600">
          <div className="flex items-start gap-2"><Check size={12} className="text-emerald-500 mt-1 flex-shrink-0" /> Every submission creates a new lead in CRM with the source labelled <span className="mono text-slate-800">hexa-{widget.kind}</span>.</div>
          <div className="flex items-start gap-2"><Check size={12} className="text-emerald-500 mt-1 flex-shrink-0" /> Works on any HTML site — WordPress, Wix, Framer, Shopify, plain HTML.</div>
          <div className="flex items-start gap-2"><Check size={12} className="text-emerald-500 mt-1 flex-shrink-0" /> Style is fully isolated inside a Shadow DOM — won&apos;t clash with your site CSS.</div>
          {widget.kind === "assistant" && (
            <div className="flex items-start gap-2"><Check size={12} className="text-emerald-500 mt-1 flex-shrink-0" /> Visitors provide Name / Email / Phone first, then chat with HEXA Buddy. The whole transcript lands on the lead&apos;s activity timeline.</div>
          )}
          {widget.kind === "ai-phone" && (
            <>
              <div className="flex items-start gap-2"><Check size={12} className="text-emerald-500 mt-1 flex-shrink-0" /> Visitors tap the floating phone icon and talk — the AI agent asks for name, phone, email &amp; product interest by voice in Hindi, English &amp; other Indian languages.</div>
              <div className="flex items-start gap-2"><Check size={12} className="text-emerald-500 mt-1 flex-shrink-0" /> The full voice transcript lands on the lead&apos;s activity timeline once the call ends.</div>
            </>
          )}
        </div>

        <div className="mt-4 pt-3 border-t border-slate-200 flex items-center justify-between text-[11.5px]">
          <a href={src} target="_blank" rel="noreferrer"
            className="text-indigo-600 hover:text-indigo-800 flex items-center gap-1 mono text-[10.5px]">
            <ExternalLink size={11} /> Preview embed script
          </a>
          <span className="text-slate-400 mono text-[10.5px]">{widget.id}</span>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────  IVR RING-GROUPS  ───────────────────────────── */

function IvrPanel() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try { const r = await api.get("/crm/ivr"); setRows(r.data || []); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"? Incoming calls to ${r.ivr_number} will stop routing.`)) return;
    try { await api.delete(`/crm/ivr/${r.id}`); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-slate-800">
          <div className="text-base font-semibold flex items-center gap-2">
            <Phone size={16} className="text-amber-500" /> IVR ring-groups
          </div>
          <div className="text-xs text-slate-500 mt-0.5">A customer calls one number → HEXA rings every attached sales-person in parallel. Whoever picks up owns the lead. Every call is recorded.</div>
        </div>
        <button onClick={() => setAdding(true)} data-testid="ivr-new"
          className="text-[11px] px-3 py-1.5 rounded text-white font-medium flex items-center gap-1"
          style={{ background: "linear-gradient(120deg,#f59e0b,#ef4444)" }}>
          <Plus size={12} /> New IVR
        </button>
      </div>

      {err && <div className="text-red-600 text-xs mono">{err}</div>}

      {!busy && !rows.length && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-8 px-6 text-center">
          <Phone size={22} className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700">No IVR groups yet</div>
          <div className="text-[11.5px] text-slate-500 mt-1">Create one and point your DialNexa number&apos;s webhook at <span className="mono">{(process.env.REACT_APP_BACKEND_URL || "")}/api/webhooks/dialnexa</span>.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((r) => (
          <div key={r.id} data-testid={`ivr-${r.id}`}
            className="rounded-lg border border-slate-200 bg-white p-4">
            <div className="flex items-start gap-3">
              <div className="grid place-items-center rounded-md" style={{
                width: 34, height: 34, background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff",
              }}>
                <Phone size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 font-semibold text-[13px] truncate">{r.name}</div>
                <div className="text-[10.5px] mono text-slate-500 mt-0.5">{r.ivr_number} · {r.agents?.length || 0} agents · rings {r.ring_timeout_sec}s</div>
              </div>
              <span className="mono text-[9px] px-2 py-0.5 rounded-full uppercase tracking-widest"
                style={{
                  color: r.active ? "#059669" : "#94a3b8",
                  background: r.active ? "rgba(5,150,105,0.10)" : "#f1f5f9",
                  border: `1px solid ${r.active ? "rgba(5,150,105,0.35)" : "#e2e8f0"}`,
                }}>{r.active ? "Live" : "Paused"}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-1">
              {(r.agents || []).slice(0, 5).map((a, i) => (
                <span key={i} className="text-[10.5px] px-2 py-0.5 rounded bg-slate-100 text-slate-600 mono">
                  {a.label || a.email} · {a.phone}
                </span>
              ))}
              {r.agents && r.agents.length > 5 && (
                <span className="text-[10.5px] px-2 py-0.5 rounded bg-slate-100 text-slate-500 mono">+{r.agents.length - 5} more</span>
              )}
            </div>
            <div className="flex items-center gap-1.5 mt-3">
              <button onClick={() => setEditing(r)}
                className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">Edit</button>
              <button onClick={() => remove(r)}
                className="text-[11px] px-3 py-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50 ml-auto">
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {(adding || editing) && (
        <IvrEditor
          existing={editing}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function IvrEditor({ existing, onClose, onSaved }) {
  const [f, setF] = useState({
    name: existing?.name || "",
    ivr_number: existing?.ivr_number || "",
    ring_timeout_sec: existing?.ring_timeout_sec || 25,
    fallback_owner: existing?.fallback_owner || "",
    active: existing?.active ?? true,
    agents: existing?.agents ? [...existing.agents] : [{ email: "", phone: "", label: "" }],
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const setAgent = (idx, patch) => setF({ ...f, agents: f.agents.map((a, i) => i === idx ? { ...a, ...patch } : a) });
  const addAgent = () => setF({ ...f, agents: [...f.agents, { email: "", phone: "", label: "" }] });
  const removeAgent = (idx) => setF({ ...f, agents: f.agents.filter((_, i) => i !== idx) });

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const body = { ...f, agents: f.agents.filter(a => a.phone && a.phone.trim()) };
      if (existing) await api.put(`/crm/ivr/${existing.id}`, body);
      else          await api.post("/crm/ivr", body);
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-xl p-5 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="grid place-items-center rounded-md" style={{ width: 36, height: 36, background: "linear-gradient(135deg,#f59e0b,#ef4444)", color: "#fff" }}>
            <Phone size={16} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-800">{existing ? "Edit IVR" : "New IVR"}</div>
            <div className="text-[11.5px] text-slate-500">Configure the ring-group and its attached sales agents.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={16} /></button>
        </div>

        <div className="space-y-3">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Name</div>
            <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
              className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Public IVR number</div>
            <input value={f.ivr_number} onChange={(e) => setF({ ...f, ivr_number: e.target.value })}
              placeholder="+91 90000 00000"
              className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Ring timeout (sec)</div>
              <input type="number" min={5} max={90} value={f.ring_timeout_sec}
                onChange={(e) => setF({ ...f, ring_timeout_sec: parseInt(e.target.value || "25", 10) })}
                className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none" />
            </label>
            <label className="block">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Fallback owner email</div>
              <input value={f.fallback_owner || ""}
                onChange={(e) => setF({ ...f, fallback_owner: e.target.value })}
                placeholder="boss@yourcompany.com"
                className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none" />
            </label>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Agents ({f.agents.length})</div>
              <button onClick={addAgent}
                className="text-[11px] px-2 py-1 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
                <Plus size={11} /> Add agent
              </button>
            </div>
            <div className="space-y-2">
              {f.agents.map((a, idx) => (
                <div key={idx} className="border border-slate-200 rounded-md p-2 bg-slate-50 grid grid-cols-12 gap-2 items-center">
                  <input value={a.label || ""} onChange={(e) => setAgent(idx, { label: e.target.value })}
                    placeholder="Label" className="col-span-3 px-2 py-1.5 rounded border border-slate-200 bg-white text-sm outline-none" />
                  <input value={a.email || ""} onChange={(e) => setAgent(idx, { email: e.target.value })}
                    placeholder="agent@yourco.com" className="col-span-4 px-2 py-1.5 rounded border border-slate-200 bg-white text-xs mono outline-none" />
                  <input value={a.phone || ""} onChange={(e) => setAgent(idx, { phone: e.target.value })}
                    placeholder="+91 90000 00000" className="col-span-4 px-2 py-1.5 rounded border border-slate-200 bg-white text-xs mono outline-none" />
                  <button onClick={() => removeAgent(idx)}
                    className="col-span-1 text-rose-500 hover:text-rose-700 justify-self-end">
                    <Trash2 size={13} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 text-[12px] text-slate-700 mt-2">
            <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} />
            Active
          </label>

          {err && <div className="text-red-600 text-xs mono">{err}</div>}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 mt-4 border-t border-slate-200">
          <button onClick={onClose} className="text-[12px] text-slate-500 px-3 py-2">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="ivr-save"
            className="text-[12px] px-4 py-2 rounded text-white font-medium flex items-center gap-1.5"
            style={{ background: "linear-gradient(120deg,#f59e0b,#ef4444)" }}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─────────────────────────────  SECRETS  ───────────────────────────── */

function SecretsPanel() {
  const [secrets, setSecrets] = useState([]);
  const [providers, setProviders] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [editing, setEditing] = useState(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setBusy(true); setErr("");
    try {
      const [sec, prov] = await Promise.all([
        api.get("/admin/secrets"),
        api.get("/admin/secrets/providers"),
      ]);
      setSecrets(sec.data || []);
      setProviders(prov.data || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (s) => {
    if (!window.confirm(`Delete "${s.label}" (${s.provider})? This can't be undone.`)) return;
    try { await api.delete(`/admin/secrets/${s.id}`); await load(); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-slate-800">
          <div className="text-base font-semibold flex items-center gap-2">
            <KeyRound size={16} className="text-purple-500" /> Secrets vault
          </div>
          <div className="text-xs text-slate-500 mt-0.5">Admin-only. Sealed at rest. Staff can never see or list these.</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50 flex items-center gap-1">
            <RefreshCw size={11} /> Refresh
          </button>
          <button onClick={() => setAdding(true)} data-testid="admin-secret-new"
            className="text-[11px] px-3 py-1.5 rounded text-white font-medium flex items-center gap-1"
            style={{ background: "linear-gradient(120deg,#a855f7,#ec4899)" }}>
            <Plus size={12} /> Add secret
          </button>
        </div>
      </div>

      {err && <div className="text-red-600 text-xs mono">{err}</div>}

      {!busy && !secrets.length && (
        <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 py-8 px-6 text-center">
          <Lock size={22} className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm font-medium text-slate-700">Vault is empty</div>
          <div className="text-[11.5px] text-slate-500 mt-1">Store Zeptomail, DialNexa Voice, or Composio WhatsApp keys here so HEXA can send emails, place calls, and message on WhatsApp on your behalf.</div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {secrets.map((s) => (
          <div key={s.id} data-testid={`admin-secret-${s.id}`}
            className="rounded-lg border border-slate-200 bg-white p-4 flex flex-col gap-2">
            <div className="flex items-start gap-3">
              <div className="grid place-items-center rounded-md" style={{
                width: 34, height: 34,
                background: "linear-gradient(135deg,#a855f7,#ec4899)",
                color: "#fff",
              }}>
                <KeyRound size={15} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-slate-800 font-semibold text-[13px] truncate">{s.label}</div>
                <div className="text-[10.5px] mono uppercase tracking-wider text-slate-500 mt-0.5">{s.provider}</div>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-1 text-[11.5px] mono">
              {Object.entries(s.masked || {}).map(([k, v]) => (
                <div key={k} className="flex items-center gap-2 text-slate-600 truncate">
                  <span className="text-slate-400 lowercase">{k}</span>
                  <span className="text-slate-800 truncate">{v || "—"}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              <button onClick={() => setEditing(s)} data-testid={`admin-secret-edit-${s.id}`}
                className="text-[11px] px-3 py-1.5 rounded border border-slate-200 text-slate-600 hover:bg-slate-50">Rotate keys</button>
              <button onClick={() => remove(s)} data-testid={`admin-secret-delete-${s.id}`}
                className="text-[11px] px-3 py-1.5 rounded border border-rose-200 text-rose-600 hover:bg-rose-50 ml-auto">
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {(adding || editing) && (
        <SecretEditor
          existing={editing}
          providers={providers}
          onClose={() => { setAdding(false); setEditing(null); }}
          onSaved={() => { setAdding(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function Lock({ size = 18, className = "", ...rest }) {
  // small local icon so we don't grow the lucide import list
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" className={className} {...rest}>
      <rect x="3" y="11" width="18" height="11" rx="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function SecretEditor({ existing, providers, onClose, onSaved }) {
  const [provider, setProvider] = useState(existing?.provider || (providers[0]?.key || "custom"));
  const [label, setLabel] = useState(existing?.label || "");
  const [values, setValues] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reveal, setReveal] = useState(false);

  const providerCfg = providers.find(p => p.key === provider) || { fields: ["api_key"] };

  useEffect(() => {
    // reset the values map when the provider changes (fields differ per provider)
    setValues({});
  }, [provider]);

  const save = async () => {
    setBusy(true); setErr("");
    try {
      if (existing) {
        await api.put(`/admin/secrets/${existing.id}`, { provider, label, values });
      } else {
        await api.post("/admin/secrets", { provider, label, values });
      }
      onSaved();
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[400] grid place-items-center p-4 bg-slate-900/50" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}
        style={{ border: "1px solid #e2e8f0", boxShadow: "0 24px 60px rgba(15,23,42,0.2)" }}>
        <div className="flex items-center gap-3 mb-4">
          <div className="grid place-items-center rounded-md" style={{ width: 36, height: 36, background: "linear-gradient(135deg,#a855f7,#ec4899)", color: "#fff" }}>
            <KeyRound size={16} />
          </div>
          <div className="flex-1">
            <div className="font-semibold text-slate-800">{existing ? "Rotate keys" : "Add secret"}</div>
            <div className="text-[11.5px] text-slate-500">Only the last 3 characters are displayed after saving.</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-800"><X size={16} /></button>
        </div>

        {!existing && (
          <label className="block mb-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Provider</div>
            <select value={provider} onChange={(e) => setProvider(e.target.value)}
              data-testid="secret-provider"
              className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400">
              {providers.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
            </select>
          </label>
        )}

        <label className="block mb-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Label</div>
          <input value={label} onChange={(e) => setLabel(e.target.value)}
            placeholder={`My ${providerCfg.label || provider}`}
            data-testid="secret-label"
            className="w-full px-3 py-2 rounded border border-slate-200 text-sm outline-none focus:border-indigo-400" />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Keys ({providerCfg.fields?.length || 0})</div>
            <button onClick={() => setReveal(v => !v)} className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1">
              {reveal ? <><EyeOff size={11} /> Hide</> : <><Eye size={11} /> Show</>}
            </button>
          </div>
          {(providerCfg.fields || []).map((k) => (
            <label key={k} className="block">
              <div className="text-[10.5px] text-slate-500 mono lowercase mb-0.5">{k}</div>
              <input
                type={reveal ? "text" : "password"}
                value={values[k] || ""}
                onChange={(e) => setValues({ ...values, [k]: e.target.value })}
                placeholder={existing ? "•••••• (leave empty to keep current)" : "paste key…"}
                data-testid={`secret-field-${k}`}
                className="w-full px-3 py-2 rounded border border-slate-200 text-sm mono outline-none focus:border-indigo-400" />
            </label>
          ))}
        </div>

        {err && <div className="text-red-600 text-xs mono mt-2">{err}</div>}

        <div className="flex items-center justify-end gap-2 mt-4 pt-3 border-t border-slate-200">
          <button onClick={onClose} className="text-[12px] text-slate-500 px-3 py-2">Cancel</button>
          <button onClick={save} disabled={busy} data-testid="secret-save"
            className="text-[12px] px-4 py-2 rounded text-white font-medium flex items-center gap-1.5"
            style={{ background: "linear-gradient(120deg,#a855f7,#ec4899)" }}>
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
          </button>
        </div>
      </div>
    </div>
  );
}
